<script setup lang="ts">
/**
 * SiteFooter — 4 列 grid
 * 1. 品牌区（最左）：BrandMark + brandDesc
 * 2. 产品链接
 * 3. 法律链接
 * 4. 关注我们（最右）：公众号 + 反馈群 双二维码（点击放大）
 * 底部署名 + ICP 备案（满宽）
 *
 * 产品链接全部走 useAnchorNav，包括跨页 hash（/privacy → /#features），
 * 解决之前用原生 <a href> 跨页 hash 不滚动的 bug。
 * 见 docs/mainadr/0008-anchor-navigation.md
 */
import { useI18n } from 'vue-i18n'
import BrandMark from '@/components/BrandMark.vue'
import YfbIcon from '@/components/YfbIcon.vue'
import FooterWechatLightbox from '@/components/FooterWechatLightbox.vue'
import { useAnchorNav } from '@/composables/useAnchorNav'

const { t } = useI18n()
const { go } = useAnchorNav()

interface LinkItem {
  key: string
  href: string
}

const productLinks: LinkItem[] = [
  { key: 'membership', href: '/subscribe' },
  { key: 'features', href: '/#features' },
  { key: 'install', href: '/install' },
  { key: 'guide', href: '/guide' },
  { key: 'matrix', href: '/#matrix' },
  { key: 'faq', href: '/#faq' },
  { key: 'about', href: '/about' }
]

const legalLinks: LinkItem[] = [
  { key: 'privacy', href: '/privacy' },
  { key: 'terms', href: '/terms' },
  { key: 'refund', href: '/refund' },
  { key: 'invite', href: '/invite-rules' }
]

function handleLink(href: string) {
  void go(href)
}

// 公众号：本地资源；反馈群：在线 URL（提供方为 隐分宝官网托管）
const wechatGzh = {
  src: '/icons/qrcode_gzh.webp',
  titleKey: 'footer.social.wechatGzh.title',
  hintKey: 'footer.social.wechatGzh.hint'
}
const wechatGroup = {
  src: 'https://woofootball.site/upload/footballhub/site/wechat_group.webp',
  titleKey: 'footer.social.wechatGroup.title',
  hintKey: 'footer.social.wechatGroup.hint'
}

const icpBeian = '陕ICP备2025063718号-1'
const beianUrl = 'https://beian.miit.gov.cn/'
</script>

<template>
  <footer class="site-footer">
    <div class="site-footer__grid">
      <!-- 列 1：品牌区 -->
      <div class="site-footer__col site-footer__col--brand">
        <BrandMark size="md" variant="full" align="left" mono />
        <p class="site-footer__desc">{{ t('footer.brandDesc') }}</p>
      </div>

      <!-- 列 2：产品 -->
      <div class="site-footer__col">
        <h3 class="site-footer__col-title">{{ t('footer.sections.product') }}</h3>
        <ul class="site-footer__list">
          <li v-for="link in productLinks" :key="link.key">
            <a
              :href="link.href"
              class="site-footer__link"
              @click.prevent="handleLink(link.href)"
            >{{ t(`footer.links.${link.key}`) }}</a>
          </li>
        </ul>
      </div>

      <!-- 列 3：法律 -->
      <div class="site-footer__col">
        <h3 class="site-footer__col-title">{{ t('footer.sections.legal') }}</h3>
        <ul class="site-footer__list">
          <li v-for="link in legalLinks" :key="link.key">
            <a
              :href="link.href"
              class="site-footer__link"
              @click.prevent="handleLink(link.href)"
            >{{ t(`footer.links.${link.key}`) }}</a>
          </li>
        </ul>
      </div>

      <!-- 列 4：关注我们 -->
      <div class="site-footer__col site-footer__col--social">
        <h3 class="site-footer__col-title">{{ t('footer.sections.social') }}</h3>
        <div class="site-footer__qrs">
          <div class="site-footer__qr-item">
            <FooterWechatLightbox
              :src="wechatGzh.src"
              :alt="t(wechatGzh.titleKey)"
              :title="t(wechatGzh.titleKey)"
            />
            <span class="site-footer__qr-label">{{ t(wechatGzh.titleKey) }}</span>
          </div>
          <div class="site-footer__qr-item">
            <FooterWechatLightbox
              :src="wechatGroup.src"
              :alt="t(wechatGroup.titleKey)"
              :title="t(wechatGroup.titleKey)"
            />
            <span class="site-footer__qr-label">{{ t(wechatGroup.titleKey) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部署名 + ICP -->
    <div class="site-footer__bottom">
      <p class="site-footer__copyright">{{ t('footer.copyright') }}</p>
      <p class="site-footer__beian">
        <a :href="beianUrl" target="_blank" rel="noopener noreferrer">
          <YfbIcon name="lucide:shield-check" :size="14" />
          {{ icpBeian }}
        </a>
      </p>
    </div>
  </footer>
</template>

<style scoped>
.site-footer {
  background-color: var(--yfb-surface);
  color: var(--yfb-ink);
  border-top: 1px solid var(--yfb-glass-border);
}

.site-footer__grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1.5fr;
  gap: var(--yfb-sp-8);
  padding: var(--yfb-sp-8) var(--yfb-sp-6) var(--yfb-sp-7);
  max-width: 1200px;
  margin: 0 auto;
}

/* 列 1 品牌 */
.site-footer__col--brand {
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-4);
  padding-right: 24px;
  border-right: 1px solid var(--yfb-rule-soft);
}

.site-footer__desc {
  margin: 0;
  font-size: var(--yfb-fs-sm);
  font-weight: 400;
  line-height: var(--yfb-lh-lead);
  color: var(--yfb-text-secondary);
  max-width: 360px;
}

/* 列 2/3/4 链接 */
.site-footer__col-title {
  font-family: var(--yfb-font-heading);
  font-size: var(--yfb-fs-xs);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-eyebrow);
  color: var(--yfb-accent);
  margin: 0 0 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--yfb-rule);
}

.site-footer__list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: calc(var(--yfb-sp-2) + var(--yfb-sp-1) / 2);
}

.site-footer__link {
  color: var(--yfb-ink);
  text-decoration: none;
  font-size: var(--yfb-fs-sm);
  font-weight: 400;
  transition: color var(--yfb-anim-fast) ease-out, text-shadow var(--yfb-anim-fast) ease-out;
}

.site-footer__link:hover {
  color: var(--yfb-accent);
  text-shadow: 0 0 10px var(--yfb-glow);
}

.site-footer__link:focus-visible {
  outline: 2px solid var(--yfb-focus);
  outline-offset: 2px;
}

/* 列 4 二维码 */
.site-footer__col--social {
  padding-left: 8px;
}

.site-footer__qrs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--yfb-sp-4);
}

.site-footer__qr-item {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: calc(var(--yfb-sp-1) + var(--yfb-sp-1) / 2);
}

/* FooterWechatLightbox 触发按钮在深色底下的覆盖 */
.site-footer__qrs :deep(.wechat-trigger) {
  border-color: var(--yfb-rule);
  background-color: transparent;
  padding: var(--yfb-sp-2);
  border-radius: var(--yfb-radius-sm);
}

.site-footer__qrs :deep(.wechat-trigger:hover) {
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
}

.site-footer__qrs :deep(.wechat-trigger__img) {
  width: 72px;
  height: 72px;
  background-color: var(--yfb-surface);
  padding: var(--yfb-sp-1);
  border-radius: var(--yfb-radius-sm);
}

/* lightbox 内嵌的 title 在 footer 上下文隐藏（外部 label 替代） */
.site-footer__qrs :deep(.wechat-trigger__title) {
  display: none;
}

.site-footer__qr-label {
  display: block;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
  color: var(--yfb-text-secondary);
  text-align: center;
  font-family: var(--yfb-font-heading);
  line-height: 1.3;
}

/* 底部 */
.site-footer__bottom {
  padding: var(--yfb-sp-5) var(--yfb-sp-6);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--yfb-sp-2);
  border-top: 1px solid var(--yfb-rule-soft);
}

.site-footer__copyright {
  margin: 0;
  font-size: var(--yfb-fs-xs);
  color: var(--yfb-ink-soft);
  text-align: center;
}

.site-footer__beian {
  margin: 0;
  font-size: var(--yfb-fs-xs);
}

.site-footer__beian a {
  display: inline-flex;
  align-items: center;
  gap: calc(var(--yfb-sp-1) + var(--yfb-sp-1) / 2);
  color: var(--yfb-ink-soft);
  text-decoration: none;
  font-family: var(--yfb-mono);
  transition: color var(--yfb-anim-fast) ease-out;
}

.site-footer__beian a:hover {
  color: var(--yfb-accent);
}

@media (max-width: 1023px) {
  .site-footer__grid {
    grid-template-columns: 1fr 1fr;
    gap: var(--yfb-sp-7);
    padding: var(--yfb-sp-8) var(--yfb-sp-6) var(--yfb-sp-7);
  }

  .site-footer__col--brand {
    grid-column: 1 / -1;
    border-right: none;
    border-bottom: 1px solid var(--yfb-rule-soft);
    padding-right: 0;
    padding-bottom: 24px;
  }
}

@media (max-width: 767px) {
  .site-footer__grid {
    grid-template-columns: 1fr;
    gap: var(--yfb-sp-7);
    padding: var(--yfb-sp-7) var(--yfb-sp-4) var(--yfb-sp-5);
  }

  .site-footer__col--brand {
    border-bottom: 1px solid var(--yfb-rule-soft);
  }

  .site-footer__bottom {
    padding-left: 16px;
    padding-right: 16px;
  }
}
</style>
